import { Check } from '@react-apps/design-system';
import type { QuestionBody } from '../../domain/entities/content.entity';
import { sanitizeQuestionPreview } from '@react-apps/content-editor';
import { isAnnulled } from '../answer-key';
import { AnnulledNotice } from './AnnulledNotice';

/**
 * Como uma questão **se lê**: enunciado, imagens, alternativas e qual é a
 * correta. Usado nos dois lugares que precisam mostrar isso — o card em modo
 * leitura e a aba **Preview** do modo edição.
 *
 * É o mesmo componente de propósito: uma segunda renderização "parecida" é
 * exatamente como preview e resultado final se descolam, e aí o preview deixa
 * de responder a pergunta que existe para responder.
 *
 * Espera o HTML **já com as imagens resolvidas** (`<img>` no lugar do token) —
 * daí a sanitização própria, que aceita `img`. No card de leitura isso vem
 * pronto do backend; na aba Preview, o HTML do editor já traz o `<img>` do nó.
 */
export function QuestionBodyView({ body }: { body: QuestionBody }) {
  const annulled = isAnnulled(body);

  return (
    <div className="space-y-3">
      {annulled && <AnnulledNotice />}
      <QuestionHtml html={body.stem} />
      {body.format === 'MULTIPLE_CHOICE' && (
        <ul className="space-y-2">
          {body.alternatives.map((alt) => {
            // Anulada não pinta alternativa nenhuma de verde: uma questão
            // anulada com "correta" destacada é exatamente a leitura errada.
            const correct = !annulled && alt.id === body.correctAlternativeId;
            return (
              <li
                key={alt.id}
                className={[
                  // `relative` pelo mesmo motivo do `Switch`: o `sr-only`
                  // abaixo é `position: absolute`, e sem contentor posicionado
                  // aqui ele escapa para o `<body>` — 79 cards fechados viravam
                  // 79 spans a milhares de px documento abaixo, deixando a
                  // página inteira rolável para fora do shell `h-screen`.
                  'relative flex items-center gap-2 rounded-lg border p-2',
                  correct
                    ? 'border-green-500/50 bg-green-500/10'
                    : 'border-(--color-border)',
                ].join(' ')}
              >
                <span className="text-(--color-text-secondary) w-5 shrink-0 text-center text-sm font-medium uppercase">
                  {alt.id}
                </span>
                <div className="min-w-0 flex-1">
                  <QuestionHtml html={alt.text} />
                </div>
                {/* Ícone, não a palavra "correta": o verde da linha já diz que
                    é ela, e o rótulo escrito repetia isso ocupando largura em
                    cada questão da prova. O nome acessível fica no `title`, que
                    é o que um leitor de tela anuncia. */}
                {correct && (
                  <>
                    {/* O `Icon` do design-system é `aria-hidden` por contrato,
                        então o nome vai num texto só para leitor de tela — em
                        vez de brigar com a API do componente. */}
                    <span className="sr-only">Alternativa correta</span>
                    <Check
                      size="sm"
                      className="shrink-0 text-green-600 dark:text-green-400"
                    />
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {/* A dissertativa mostra a resposta esperada no lugar das alternativas:
          é o que quem importou ou editou precisa conferir que entrou. Sem ela,
          o aviso — "não cadastrada" é a pendência que o conteúdo procura. */}
      {body.format === 'ESSAY' &&
        (body.expectedAnswer ? (
          <div className="border-(--color-border) space-y-1 rounded-lg border p-2">
            <p className="text-(--color-text-secondary) text-xs font-medium">
              Resposta esperada
            </p>
            <QuestionHtml html={body.expectedAnswer} />
          </div>
        ) : (
          <p className="text-(--color-text-secondary) text-xs">
            Sem resposta esperada cadastrada.
          </p>
        ))}
    </div>
  );
}

function QuestionHtml({ html }: { html: string }) {
  return (
    <div
      // `rich-text-read` dá à figura o tamanho de leitura — normalizado numa
      // caixa só (ver `globals.css`), e não o natural: numa prova de 80, figura
      // em tamanho natural faz uma questão ocupar a tela e a seguinte caber
      // numa linha, e comparar as duas é o trabalho desta tela.
      className="rich-text rich-text-read text-(--color-text-primary) text-sm"
      // Sanitizado no cliente antes de renderizar (allowlist + `img`).
      dangerouslySetInnerHTML={{ __html: sanitizeQuestionPreview(html) }}
    />
  );
}
